import React, { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  LayoutDashboard,
  FileText,
  Search,
  Target,
  Bookmark,
  Globe,
  Briefcase,
  Settings,
  LogOut,
  Languages,
  ChevronDown,
  Sliders,
  Filter,
  MessageSquare,
  Mic,
  PenTool,
  BrainCircuit,
  Linkedin,
  LineChart,
  Banknote,
  Lightbulb,
  Building,
  Sparkles
} from 'lucide-react';
import toastUtil from '../../utils/toast';

export default function Sidebar({ isCollapsed, setIsCollapsed }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, toggleLanguage: toggleGlobalLanguage, isRTL } = useTheme();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [jobSearchOpen, setJobSearchOpen] = useState(false);
  const [aiDocsOpen, setAiDocsOpen] = useState(true);
  const [aiCareerOpen, setAiCareerOpen] = useState(true);

  const sidebarTranslations = {
    en: {
      overview: 'Overview',
      cvBuilder: 'CV Builder',
      jobSearch: 'Job Search',
      freeJobSearch: 'Free Job Search',
      jobMatch: 'Job Match AI',
      savedJobs: 'Job Library',
      remoteJobs: 'Remote Jobs',
      advancedSearch: 'Advanced Search',
      coverLetters: 'Cover Letters',
      chatbot: 'AI Agent',
      interviewPrep: 'Interview Simulator',
      careerGrowth: 'Career Growth',
      coverLetterGen: 'Cover Letter Generator',
      cvTranslator: 'CV Translator',
      atsMatcher: 'ATS Keyword Matcher',
      skillGap: 'Skill Gap Analysis',
      linkedinOpt: 'LinkedIn Optimizer',
      salaryPredictor: 'Salary Predictor',
      projectGen: 'Project Generator',
      culturalFit: 'Cultural Fit Analyzer',
      settings: 'Settings',
      logout: 'Logout',
      language: 'Language',
      portfolioBuilder: 'Portfolio Builder'
    },
    ar: {
      overview: 'نظرة عامة',
      cvBuilder: 'منشئ السيرة الذاتية',
      jobSearch: 'البحث عن وظائف',
      freeJobSearch: 'بحث مجاني عن وظائف',
      jobMatch: 'مطابقة الوظائف',
      savedJobs: 'مكتبة الوظائف',
      remoteJobs: 'وظائف عن بعد',
      advancedSearch: 'بحث متقدم',
      coverLetters: 'رسائل التغطية',
      chatbot: 'وكيل الذكاء الاصطناعي',
      interviewPrep: 'محاكي المقابلات',
      careerGrowth: 'التطوير المهني',
      coverLetterGen: 'صانع رسائل التغطية',
      cvTranslator: 'مترجم السيرة الذاتية',
      atsMatcher: 'مطابق الـ ATS',
      skillGap: 'فجوة المهارات',
      linkedinOpt: 'مُحسن لينكد إن',
      salaryPredictor: 'متوقع الرواتب',
      projectGen: 'صانع المشاريع العملية',
      culturalFit: 'محلل ثقافة الشركات',
      settings: 'الإعدادات',
      logout: 'تسجيل الخروج',
      language: 'اللغة',
      portfolioBuilder: 'منشئ معرض الأعمال'
    },
    tr: {
      overview: 'Genel Bakış',
      cvBuilder: 'CV Oluşturucu',
      jobSearch: 'İş Arama',
      freeJobSearch: 'Ücretsiz İş Arama',
      jobMatch: 'İş Eşleştirme Yapay Zekası',
      savedJobs: 'İş Kütüphanesi',
      remoteJobs: 'Uzaktan İşler',
      advancedSearch: 'Gelişmiş Arama',
      coverLetters: 'Ön Yazılar',
      chatbot: 'Yapay Zeka Ajanı',
      interviewPrep: 'Mülakat Simülatörü',
      careerGrowth: 'Kariyer Gelişimi',
      coverLetterGen: 'Ön Yazı Oluşturucu',
      cvTranslator: 'CV Çevirmen',
      atsMatcher: 'ATS Anahtar Kelime Eşleştirici',
      skillGap: 'Beceri Boşluğu Analizi',
      linkedinOpt: 'LinkedIn Optimize Edici',
      salaryPredictor: 'Maaş Tahmin Edici',
      projectGen: 'Proje Oluşturucu',
      culturalFit: 'Kültürel Uyum Analizörü',
      settings: 'Ayarlar',
      logout: 'Çıkış Yap',
      language: 'Dil',
      portfolioBuilder: 'Portföy Oluşturucu'
    }
  };

  const tr = sidebarTranslations[language] || sidebarTranslations['en'];

  const jobSearchSubItems = [
    { id: 'free-job-search', icon: Search, label: tr.freeJobSearch, path: '/job-search' },
    { id: 'remote-jobs-sub', icon: Globe, label: tr.remoteJobs, path: '/remote-jobs' },
    { id: 'advanced-search-sub', icon: Filter, label: tr.advancedSearch, path: '/advanced-search' },
  ];

  const coreItems = [
    { id: 'overview', icon: LayoutDashboard, label: tr.overview, path: '/dashboard' },
    { id: 'cv-builder', icon: FileText, label: tr.cvBuilder, path: '/cv-builder' },
    { id: 'job-search', icon: Search, label: tr.jobSearch, hasSubmenu: true },
    { id: 'saved-jobs', icon: Bookmark, label: tr.savedJobs, path: '/job-library' },
    { id: 'cover-letters', icon: FileText, label: tr.coverLetters, path: '/cover-letters' },
  ];

  const aiDocumentItems = [
    { id: 'cover-letter-gen', icon: PenTool, label: tr.coverLetterGen, path: '/cover-letter-generator' },
    { id: 'portfolio-builder', icon: Globe, label: tr.portfolioBuilder, path: '/portfolio-builder' },
    { id: 'cv-translator', icon: Languages, label: tr.cvTranslator, path: '/cv-translator' },
    { id: 'ats-matcher', icon: Search, label: tr.atsMatcher, path: '/ats-keyword-matcher' },
    { id: 'linkedin-opt', icon: Linkedin, label: tr.linkedinOpt, path: '/linkedin-optimizer' },
  ];

  const aiCareerItems = [
    { id: 'job-match', icon: Target, label: tr.jobMatch, path: '/job-match' },
    { id: 'chatbot', icon: MessageSquare, label: tr.chatbot, path: '/chatbot' },
    { id: 'interview-prep', icon: Mic, label: tr.interviewPrep, path: '/interview-prep' },
    { id: 'skill-gap', icon: LineChart, label: tr.skillGap, path: '/skill-gap-analysis' },
    { id: 'salary-predictor', icon: Banknote, label: tr.salaryPredictor, path: '/salary-predictor' },
    { id: 'project-gen', icon: Lightbulb, label: tr.projectGen, path: '/project-generator' },
    { id: 'cultural-fit', icon: Building, label: tr.culturalFit, path: '/cultural-fit' },
  ];

  const bottomItems = [
    { id: 'language', icon: Languages, label: tr.language, action: handleToggleLanguage },
    { id: 'settings', icon: Settings, label: tr.settings, path: '/settings' },
    { id: 'logout', icon: LogOut, label: tr.logout, action: handleLogout, danger: true }
  ];

  const isActivePath = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const isJobSearchActive = () => {
    return location.pathname.startsWith('/job-search') || 
           location.pathname.startsWith('/remote-jobs') || 
           location.pathname.startsWith('/advanced-search');
  };

  async function handleLogout() {
    try {
      await logout();
      toastUtil.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      toastUtil.error('Failed to logout');
    }
  }

  function handleToggleLanguage() {
    const nextLang = language === 'en' ? 'ar' : 'en';
    toggleGlobalLanguage();
    toastUtil.success(nextLang === 'ar' ? 'تم التبديل إلى العربية' : 'Switched to English');
  }

  const renderNavItem = (item, isBottom = false) => {
    const Icon = item.icon;
    const isActive = item.path && isActivePath(item.path);
    const isDanger = item.danger;
    const hasSubmenu = item.hasSubmenu;
    const isSubmenuActive = item.id === 'job-search' && isJobSearchActive();
    const handleMouseEnter = (e) => {
      setHoveredItem(item.id);
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: isRTL ? rect.left - 2 : rect.right + 2
      });
    };

    const handleClick = () => {
      if (hasSubmenu) {
        setJobSearchOpen(!jobSearchOpen);
      } else if (item.action) {
        item.action();
      }
    };

    const content = (
      <motion.div
        className={`
          relative flex items-center gap-2 px-3 py-2 rounded-full cursor-pointer group transition-all
          ${isActive || isSubmenuActive
            ? 'bg-blue-600 text-white shadow-md'
            : isDanger
            ? 'text-red-600 hover:bg-red-50'
            : 'text-slate-600 hover:bg-slate-100'
          }
          ${isCollapsed ? 'justify-center' : ''}
        `}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setHoveredItem(null)}
        whileHover={{ scale: (isActive || isSubmenuActive) ? 1 : 1.01 }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Active indicator bar */}
        {(isActive || isSubmenuActive) && (
          <motion.div
            layoutId="activeIndicator"
            className={`absolute top-0 bottom-0 w-1 bg-white ${isRTL ? 'right-0 rounded-l-full' : 'left-0 rounded-r-full'}`}
            initial={false}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          />
        )}
        
        <Icon 
          className={`
            w-4 h-4 flex-shrink-0 transition-colors
            ${(isActive || isSubmenuActive)
              ? 'text-white' 
              : isDanger
              ? 'text-red-600'
              : 'text-slate-600'
            }
          `} 
        />
        
        <AnimatePresence mode="wait">
          {!isCollapsed && (
            <span
              className={`text-sm font-medium whitespace-nowrap overflow-hidden flex-1 ${
                (isActive || isSubmenuActive) ? 'text-white' : isDanger ? 'text-red-600' : 'text-slate-700'
              }`}
            >
              {item.label}
            </span>
          )}
        </AnimatePresence>

        {/* Dropdown arrow for submenu */}
        {hasSubmenu && !isCollapsed && (
          <motion.div
            animate={{ rotate: jobSearchOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className={`w-4 h-4 ${(isActive || isSubmenuActive) ? 'text-white' : 'text-[#6B7280]'}`} />
          </motion.div>
        )}
      </motion.div>
    );

    if (item.path) {
      return (
        <Link key={item.id} to={item.path}>
          {content}
        </Link>
      );
    }

    return (
      <div key={item.id} onClick={handleClick}>
        {content}
      </div>
    );
  };

  return (
    <motion.aside
      initial={false}
      animate={{ width: isCollapsed ? '80px' : '280px' }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      className={`relative bg-white ${isRTL ? 'border-l' : 'border-r'} border-[#E5E7EB] flex flex-col h-screen sticky top-0 z-30 shadow-sm overflow-visible`}
      onMouseEnter={() => setIsCollapsed(false)}
      onMouseLeave={() => setIsCollapsed(true)}
    >
      {/* Logo Section */}
      <motion.div 
        className="py-6 border-b border-[#E5E7EB] flex items-center justify-center"
        whileHover={{ backgroundColor: 'rgba(224, 242, 255, 0.3)' }}
        transition={{ duration: 0.3 }}
      >
        <Link to="/dashboard" className="flex items-center gap-2">
          <img 
            src="/cvlogs-logo.png" 
            alt="CVLogs" 
            className="w-10 h-10 object-contain flex-shrink-0"
            style={{ imageRendering: 'crisp-edges' }}
          />
          <AnimatePresence mode="wait">
            {!isCollapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="text-xl font-bold text-[#333333] whitespace-nowrap overflow-hidden"
              >
                Logs
              </motion.span>
            )}
          </AnimatePresence>
        </Link>
      </motion.div>

      {/* Navigation Links */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto overflow-x-visible professional-scrollbar">
        {/* Core Items */}
        <div className="mb-6">
          {!isCollapsed && (
            <div className="px-4 py-2 text-xs font-semibold text-[#6B7280] uppercase tracking-wider">
              {language === 'ar' ? 'الخدمات الأساسية' : 'Core Features'}
            </div>
          )}
          {coreItems.map(item => (
            <div key={item.id}>
              {renderNavItem(item)}
              
              {/* Submenu for Job Search */}
              <AnimatePresence>
                {item.hasSubmenu && jobSearchOpen && !isCollapsed && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                    animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden ml-2"
                  >
                    <div className={`
                      bg-gradient-to-br from-[#F8FAFC] to-[#F0F4FF] 
                      rounded-xl p-2 space-y-1
                      border border-[#E0F2FF]
                      shadow-sm
                    `}>
                      {jobSearchSubItems.map((subItem, index) => {
                        const SubIcon = subItem.icon;
                        const isSubActive = isActivePath(subItem.path);
                        
                        return (
                          <Link key={subItem.id} to={subItem.path}>
                            <motion.div
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.05, duration: 0.2 }}
                              className={`
                                relative flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer
                                transition-all duration-200
                                ${isSubActive 
                                  ? 'bg-white text-[#2F80ED] shadow-md border border-[#2F80ED]/20' 
                                  : 'text-[#6B7280] hover:bg-white/70'
                                }
                              `}
                              whileHover={{ 
                                x: isSubActive ? 0 : 4,
                                scale: isSubActive ? 1 : 1.02,
                                transition: { duration: 0.2, ease: [0.16, 1, 0.3, 1] }
                              }}
                              whileTap={{ scale: 0.98 }}
                            >
                              {/* Active indicator */}
                              {isSubActive && (
                                <motion.div
                                  layoutId="subMenuActiveIndicator"
                                  className={`absolute top-0 bottom-0 w-1 bg-[#2F80ED] ${isRTL ? 'right-0 rounded-l-full' : 'left-0 rounded-r-full'}`}
                                  initial={false}
                                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                />
                              )}
                              
                              {/* Icon with animation */}
                              <motion.div
                                whileHover={{ rotate: isSubActive ? 0 : 5, scale: 1.1 }}
                                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                className={`
                                  flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center
                                  ${isSubActive 
                                    ? 'bg-[#2F80ED]/10' 
                                    : 'bg-white/50 group-hover:bg-white'
                                  }
                                `}
                              >
                                <SubIcon className={`w-4 h-4 ${isSubActive ? 'text-[#2F80ED]' : 'text-[#6B7280]'}`} />
                              </motion.div>
                              
                              {/* Label */}
                              <span className={`
                                text-sm font-semibold transition-colors duration-200
                                ${isSubActive ? 'text-[#2F80ED]' : 'text-[#4B5563]'}
                              `}>
                                {subItem.label}
                              </span>
                              
                              {/* Hover indicator */}
                              {!isSubActive && (
                                <motion.div
                                  initial={{ opacity: 0, scale: 0 }}
                                  whileHover={{ opacity: 1, scale: 1 }}
                                  className="ml-auto w-1.5 h-1.5 rounded-full bg-[#2F80ED]/30"
                                />
                              )}
                            </motion.div>
                          </Link>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        {/* AI Document Features */}
        <div className="mb-6">
          {!isCollapsed && (
            <div 
              onClick={() => setAiDocsOpen(!aiDocsOpen)}
              className="px-4 py-2 text-xs font-semibold flex items-center justify-between text-[#2F80ED] uppercase tracking-wider cursor-pointer hover:bg-[#F0F4FF] rounded-lg transition-colors mx-2"
            >
              <div className="flex items-center gap-2">
                <FileText className="w-3 h-3" />
                {language === 'ar' ? 'أدوات المستندات' : 'Document AI'}
              </div>
              <motion.div animate={{ rotate: aiDocsOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className="w-4 h-4 text-[#2F80ED]" />
              </motion.div>
            </div>
          )}
          <AnimatePresence>
            {(aiDocsOpen || isCollapsed) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden"
              >
                {aiDocumentItems.map(item => (
                  <div key={item.id}>
                    {renderNavItem(item)}
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* AI Career Features */}
        <div className="mb-6">
          {!isCollapsed && (
            <div 
              onClick={() => setAiCareerOpen(!aiCareerOpen)}
              className="px-4 py-2 text-xs font-semibold flex items-center justify-between text-[#2F80ED] uppercase tracking-wider cursor-pointer hover:bg-[#F0F4FF] rounded-lg transition-colors mx-2"
            >
              <div className="flex items-center gap-2">
                <Target className="w-3 h-3" />
                {language === 'ar' ? 'أدوات المسار المهني' : 'Career AI'}
              </div>
              <motion.div animate={{ rotate: aiCareerOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className="w-4 h-4 text-[#2F80ED]" />
              </motion.div>
            </div>
          )}
          <AnimatePresence>
            {(aiCareerOpen || isCollapsed) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden"
              >
                {aiCareerItems.map(item => (
                  <div key={item.id}>
                    {renderNavItem(item)}
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </nav>

      {/* Bottom Actions */}
      <div className="p-4 border-t border-[#E5E7EB] space-y-1">
        {bottomItems.map(item => renderNavItem(item, true))}
      </div>

      {/* Global Tooltip - Fixed Positioning Outside Overflow */}
      <AnimatePresence>
        {isCollapsed && hoveredItem && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, x: -10 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.95, x: -10 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'fixed',
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: `translate(${isRTL ? '-100%' : '0'}, -50%)`,
              zIndex: 9999
            }}
            className="bg-[#333333] text-white px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap shadow-xl pointer-events-none border border-[#4B5563] text-left"
          >
            {[...coreItems, ...aiDocumentItems, ...aiCareerItems, ...bottomItems].find(item => item.id === hoveredItem)?.label}
            {/* Tooltip Arrow */}
            <div 
              className={`absolute top-1/2 -translate-y-1/2 w-0 h-0 border-[6px] border-transparent ${
                isRTL 
                  ? 'right-full -mr-1 border-r-[#333333]' 
                  : 'left-full -ml-1 border-l-[#333333]'
              }`}
              style={{
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.aside>
  );
}